import React, { useEffect, useMemo, useState } from "react";
import { Button, Result } from "antd";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  getHubPlanId,
  resolveUserSubscriptionAccess,
  SUBSCRIPTION_STATE,
} from "../utils/subscriptionState";
import { SUBSCRIPTION_GATE_LABELS } from "../labels/subscriptionGateLabels";
import {
  getSubscriptionRouteRequirement,
} from "../config/subscriptionCatalogConfig";
import {
  resolveCatalogEntitlement,
} from "../services/subscriptionCatalog";

const HUB_SETUP_ROUTE = "/hub";
const SUBSCRIPTION_ROUTE = "/subscription";

export default function SubscriptionGate({ children }) {
  const {
    token,
    decodeToken,
    logout,
    subscriptionCatalog,
    refreshSubscriptionCatalog,
    subscriptionCatalogStatus,
    getActiveStoreUsage,
    seedActiveStoreCount,
    ensureActiveStoreCount,
  } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [catalogRetryKey, setCatalogRetryKey] = useState(0);

  const decodedToken = useMemo(
    () => decodeToken(token),
    [decodeToken, token],
  );
  const access = useMemo(
    () => resolveUserSubscriptionAccess(decodedToken),
    [decodedToken],
  );
  const requirement = useMemo(
    () => getSubscriptionRouteRequirement(location.pathname),
    [location.pathname],
  );
  const requestedHubId = location.state?.selectedHubId || access.hubProfile?.PK || null;
  const requestedHubAccess = useMemo(() => {
    const ownerProfile = (decodedToken?.MyHubAdmin || [])
      .map((entry) => entry?.HubProfile)
      .find((profile) => profile?.PK === requestedHubId);
    if (ownerProfile) return { hubProfile: ownerProfile, role: "owner" };
    const staffProfile = (decodedToken?.StaffAssignments || [])
      .map((entry) => entry?.HubProfile)
      .find((profile) => profile?.PK === requestedHubId);
    return staffProfile
      ? { hubProfile: staffProfile, role: "staff" }
      : { hubProfile: access.hubProfile, role: access.role };
  }, [access.hubProfile, access.role, decodedToken, requestedHubId]);
  const needsStoreUsage = location.pathname === "/biz/create";
  const storeCountFromNavigation = Array.isArray(location.state?.existingBizIds)
    ? location.state.existingBizIds.length
    : null;
  const storeUsageSnapshot = getActiveStoreUsage(requestedHubId);

  useEffect(() => {
    if (!needsStoreUsage || !requestedHubId || !token) return undefined;

    if (storeCountFromNavigation !== null) {
      seedActiveStoreCount(requestedHubId, storeCountFromNavigation);
      return undefined;
    }

    if (storeUsageSnapshot.status === "ready" || storeUsageSnapshot.status === "loading") {
      return undefined;
    }

    ensureActiveStoreCount(requestedHubId).catch(() => undefined);
    return undefined;
  }, [
    catalogRetryKey,
    ensureActiveStoreCount,
    needsStoreUsage,
    requestedHubId,
    seedActiveStoreCount,
    storeCountFromNavigation,
    storeUsageSnapshot.status,
    token,
  ]);

  const entitlement = useMemo(() => {
    if (!subscriptionCatalog || !requirement) return null;
    return resolveCatalogEntitlement(
      subscriptionCatalog,
      getHubPlanId(requestedHubAccess.hubProfile),
      requirement,
      requestedHubAccess.role,
    );
  }, [requestedHubAccess.hubProfile, requestedHubAccess.role, requirement, subscriptionCatalog]);

  if (access.state === SUBSCRIPTION_STATE.HUB_SETUP_REQUIRED) {
    return location.pathname === HUB_SETUP_ROUTE
      ? children
      : <Navigate to={HUB_SETUP_ROUTE} replace />;
  }

  if (access.role === "staff" && !access.active) {
    return (
      <Result
        status="warning"
        title={SUBSCRIPTION_GATE_LABELS.staffTitle}
        subTitle={SUBSCRIPTION_GATE_LABELS.staffDescription}
        extra={
          <Button type="primary" onClick={logout}>
            {SUBSCRIPTION_GATE_LABELS.logout}
          </Button>
        }
      />
    );
  }

  if (access.active || location.pathname === SUBSCRIPTION_ROUTE) {
    if (!requirement || location.pathname === SUBSCRIPTION_ROUTE) {
      return children;
    }

    if (!subscriptionCatalog) {
      if (subscriptionCatalogStatus === "error") {
        return (
          <Result
            status="warning"
            title={SUBSCRIPTION_GATE_LABELS.catalogUnavailableTitle}
            subTitle={SUBSCRIPTION_GATE_LABELS.catalogUnavailableDescription}
            extra={
              <Button
                type="primary"
                onClick={() => {
                  refreshSubscriptionCatalog({ force: true }).catch(() => undefined);
                  setCatalogRetryKey((current) => current + 1);
                }}
              >
                {SUBSCRIPTION_GATE_LABELS.retry}
              </Button>
            }
          />
        );
      }

      // Keep routed pages mounted while the catalog loads to avoid duplicate API calls.
      return children;
    }

    const effectiveStoreCount = storeCountFromNavigation ?? storeUsageSnapshot.count;
    const storeUsageStatus = storeCountFromNavigation !== null
      ? "ready"
      : storeUsageSnapshot.status;
    const quotaReached = needsStoreUsage &&
      storeUsageStatus === "ready" &&
      Number.isFinite(entitlement?.limit) &&
      typeof effectiveStoreCount === "number" &&
      effectiveStoreCount >= entitlement.limit;

    if (!entitlement?.allowed || quotaReached) {
      return (
        <Result
          status="403"
          title={
            needsStoreUsage
              ? SUBSCRIPTION_GATE_LABELS.featureUnavailableOrLimitTitle
              : SUBSCRIPTION_GATE_LABELS.featureUnavailableTitle
          }
          subTitle={SUBSCRIPTION_GATE_LABELS.featureUnavailableDescription}
          extra={
            <Button
              type="primary"
              onClick={() => navigate(SUBSCRIPTION_ROUTE, { replace: true })}
            >
              {SUBSCRIPTION_GATE_LABELS.manageSubscription}
            </Button>
          }
        />
      );
    }

    return children;
  }

  return <Navigate to={SUBSCRIPTION_ROUTE} replace />;
}
